import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

/**
 * Sends a heartbeat to /api/progress/heartbeat every `intervalSec` seconds
 * while the tab is visible. Backend accumulates time-on-task per lesson with
 * a 2× nominal-hours cap to prevent abuse (left-open-tab inflation).
 *
 * Heartbeats pause when the tab is hidden / blurred and resume when active.
 */
export function useLessonHeartbeat(lessonId, intervalSec = 30) {
  const lastTickRef = useRef(null);
  const [minutesStudied, setMinutesStudied] = useState(null);

  useEffect(() => {
    if (!lessonId) return;

    let cancelled = false;
    let timerId = null;
    let isVisible = !document.hidden && document.hasFocus();

    const send = async (sec) => {
      try {
        const { data } = await api.post("/progress/heartbeat", {
          lesson_id: lessonId,
          seconds: Math.min(sec, 120),  // backend caps at 120 anyway
        });
        if (!cancelled && data?.minutes !== undefined) {
          setMinutesStudied(data.minutes);
        }
      } catch {
        // Silently ignore — heartbeat failures shouldn't disrupt the student
      }
    };

    const tick = () => {
      if (!isVisible) return;
      const now = Date.now();
      if (lastTickRef.current) {
        const elapsedSec = Math.floor((now - lastTickRef.current) / 1000);
        // Only send if a meaningful chunk has elapsed and tab stayed visible
        if (elapsedSec >= intervalSec && elapsedSec <= intervalSec * 4) {
          send(elapsedSec);
        }
      }
      lastTickRef.current = now;
    };

    const onVisibility = () => {
      const wasVisible = isVisible;
      isVisible = !document.hidden && document.hasFocus();
      if (isVisible && !wasVisible) {
        // Resuming — reset the timestamp so we don't double-count idle time
        lastTickRef.current = Date.now();
      }
    };

    lastTickRef.current = Date.now();
    timerId = setInterval(tick, intervalSec * 1000);
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", onVisibility);
    window.addEventListener("blur", onVisibility);

    return () => {
      cancelled = true;
      if (timerId) clearInterval(timerId);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", onVisibility);
      window.removeEventListener("blur", onVisibility);
    };
  }, [lessonId, intervalSec]);

  return { minutesStudied };
}
